import { useEffect, useMemo } from "react"
import { UseFormReturn, useForm } from "react-hook-form"
import { useSearchParams } from "react-router-dom"

import { useBuildingTypeOptions } from "shared/lib/building-references/useBuildingTypeOptions"
import { useHydratedFormValues } from "shared/lib/form/useHydratedFormValues"
import { useDebounce } from "shared/lib/performance/useDebounce"
import { useEvent } from "shared/lib/useEvent"
import { SelectOption } from "shared/types/Select"
import { BuildingType } from "shared/types/references/BuildingType"

import { BuildingReferenceFilterFormData } from "entities/building-references/types/BuildingReferenceFilterFormData"
import { ProductSelectOption } from "entities/products/types/ProductSelectOption"

import { BuildingReferenceFilterModel } from "../BuildingReferenceFilter"

export interface BuildingReferenceFilterViewModel {
  form: UseFormReturn<BuildingReferenceFilterFormData>
  formValues: BuildingReferenceFilterFormData

  productOptions: ProductSelectOption[] | undefined
  buildingTypeOptions: SelectOption<BuildingType>[]

  isLoading?: boolean

  onChange: (formData: BuildingReferenceFilterFormData) => void
}

const emptyFilterState: BuildingReferenceFilterFormData = {
  buildingTypes: [],
  products: [],
  searchTerm: ""
}

export function useBuildingReferenceFilterViewModel({
  defaultValues = emptyFilterState,
  onChange,
  ...rest
}: BuildingReferenceFilterModel): BuildingReferenceFilterViewModel {
  const [searchParams] = useSearchParams()

  const form = useForm<BuildingReferenceFilterFormData>({
    defaultValues
  })

  useHydratedFormValues(form, defaultValues)

  const formValues = form.watch()

  // Without the seed the still-empty debounced term would be written back through
  // onChange right after hydration, stripping the search from the URL again before
  // the real term arrives 300ms later.
  const debouncedSearchTerm = useDebounce(
    formValues.searchTerm,
    300,
    searchParams.get("searchTerm") ?? ""
  )

  const debouncedFormData = useMemo(
    () => ({
      searchTerm: debouncedSearchTerm,
      products: formValues.products,
      buildingTypes: formValues.buildingTypes
    }),
    [debouncedSearchTerm, formValues.products, formValues.buildingTypes]
  )

  useEffect(() => {
    onChange?.(debouncedFormData)
  }, [debouncedFormData, onChange])

  const buildingTypeOptions = useBuildingTypeOptions()

  const handleChange = useEvent((formData: BuildingReferenceFilterFormData) => {
    form.reset(formData)
  })

  return {
    ...rest,
    form,
    formValues,
    buildingTypeOptions,
    onChange: handleChange
  }
}
